import { CategoriesStore } from "@/stores/categories-store.core";
import { LegacyStore } from "@/stores/legacy-store.core";
import { Assignment2Store } from "@/stores/assignment-2-store.core";
import type {
   FindAssignmentsPaginatedQueryParams,
   FindAssignmentsPaginatedResponse,
} from "@laborchart-modules/lc-core-api/dist/api/assignments";
import { FindAssignmentsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/enums/find-assignments";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import {
   FilterClassifier,
   FilterFieldType,
} from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { useQuery } from "@tanstack/react-query";
import { PersonStore } from "@/stores/person-store.core";
import { AssignmentNestedColumns } from "../../people-list/people-list-prop-types";
import { ALL_GROUPS_ID } from "@/react/shared/constants";

export function useGetAssignmentCreationSupportDataQuery(groupId: string) {
   const query = groupId === ALL_GROUPS_ID ? {} : { group_id: groupId };
   const { data, isLoading } = useQuery({
      queryKey: ["assignment-creation-support-data", groupId],
      queryFn: () => LegacyStore.getAssignmentCreationSupportData(query).payload,
   });
   return { data: data ?? null, isLoading };
}

export function useGetCurrentAssignments(personId: string, limit: number = 2, context?: string) {
   const {
      data: currentAssignments,
      isLoading,
      refetch: fetchAssignments,
   } = useQuery({
      queryKey: ["current-assignments", personId, limit, context],
      queryFn: () => fetchCurrentAssignments(personId, limit, context),
      enabled: !!personId,
   });

   return { currentAssignments, isLoading, fetchAssignments };
}

export async function fetchCurrentAssignments(
   personId: string,
   limit: number = 2,
   context?: string,
): Promise<FindAssignmentsPaginatedResponse | null> {
   const currentDate = getDetachedDay(new Date());

   // If context is provided, use both start and end date filters for truly "current" assignments
   // Otherwise, keep current assignments banner's behavior with only end_date filter
   const endDateFilter = {
      name: "Assignment End Date",
      property: "end_date",
      type: FilterFieldType.DATE,
      value_sets: [
         {
            negation: false,
            value: currentDate,
            classifier: FilterClassifier.GREATER_THAN_OR_EQUAL,
         },
      ],
   };

   const dateFilters = [
      ...(context
         ? [
              {
                 name: "Assignment Start Date",
                 property: "start_date",
                 type: FilterFieldType.DATE,
                 value_sets: [
                    {
                       negation: false,
                       value: currentDate,
                       classifier: FilterClassifier.LESS_THAN_OR_EQUAL,
                    },
                 ],
              },
           ]
         : []),
      endDateFilter,
   ];

   const query: FindAssignmentsPaginatedQueryParams = {
      filters: [
         {
            name: "Person",
            property: "person_id",
            type: FilterFieldType.SELECT,
            value_sets: [{ negation: false, value: personId }],
         },
         ...dateFilters,
      ],
      limit,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sort_by: FindAssignmentsSortBy.ASSIGNMENT_START_DATE,
      sort_direction: Order.ASCENDING,
   };

   const data = await Assignment2Store.getAssignments(query).payload;
   if (data?.pagination) {
      const { next_starting_after, total_possible } = data.pagination;
      if (next_starting_after && total_possible > limit) {
         const nextData = await Assignment2Store.getAssignments({
            ...query,
            starting_after: next_starting_after,
            limit: total_possible,
         }).payload;
         data.data.push(...nextData.data);
      }
   }
   return data;
}

export function useGetNextAssignments(personId: string) {
   const {
      data: nextAssignments,
      isLoading: isNextAssignmentLoading,
      refetch: fetchAssignments,
   } = useQuery({
      queryKey: ["next-assignments", personId],
      queryFn: () => fetchNextAssignments(personId),
      enabled: !!personId,
   });

   return { nextAssignments, isNextAssignmentLoading, fetchAssignments };
}

export async function fetchNextAssignments(
   personId: string,
): Promise<FindAssignmentsPaginatedResponse | null> {
   const LIMIT = 10;
   const query: FindAssignmentsPaginatedQueryParams = {
      filters: [
         {
            name: "Person",
            property: "person_id",
            type: FilterFieldType.SELECT,
            value_sets: [{ negation: false, value: personId }],
         },
         {
            name: "Assignment Start Date",
            property: "start_date",
            type: FilterFieldType.DATE,
            value_sets: [
               {
                  negation: false,
                  value: getDetachedDay(new Date()),
                  classifier: FilterClassifier.GREATER_THAN,
               },
            ],
         },
         {
            name: "Assignment End Date",
            property: "end_date",
            type: FilterFieldType.DATE,
            value_sets: [
               {
                  negation: false,
                  value: getDetachedDay(new Date()),
                  classifier: FilterClassifier.GREATER_THAN_OR_EQUAL,
               },
            ],
         },
      ],
      limit: LIMIT,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sort_by: FindAssignmentsSortBy.ASSIGNMENT_START_DATE,
      sort_direction: Order.ASCENDING,
   };

   const data = await Assignment2Store.getAssignments(query).payload;
   if (data?.pagination?.next_starting_after && data?.pagination?.total_possible > LIMIT) {
      const nextData = await Assignment2Store.getAssignments({
         ...query,
         starting_after: data.pagination.next_starting_after,
         limit: data.pagination.total_possible,
      }).payload;
      data.data.push(...nextData.data);
   }
   return data;
}

export async function fetchPastAssignments(
   personId: string,
   sortModel?: Array<{ field: string; sort: "asc" | "desc" }>,
   startingAfter?: string,
): Promise<FindAssignmentsPaginatedResponse | null> {
   const LIMIT = 10;
   const query: FindAssignmentsPaginatedQueryParams = {
      filters: [
         {
            name: "Person",
            property: "person_id",
            type: FilterFieldType.SELECT,
            value_sets: [{ negation: false, value: personId }],
         },
         {
            name: "Assignment End Date",
            property: "end_date",
            type: FilterFieldType.DATE,
            value_sets: [
               {
                  negation: false,
                  value: getDetachedDay(new Date()),
                  classifier: FilterClassifier.LESS_THAN,
               },
            ],
         },
      ],
      limit: LIMIT,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sort_by: FindAssignmentsSortBy.ASSIGNMENT_END_DATE,
      sort_direction: Order.DESCENDING,
   };

   // Add starting_after for pagination
   if (startingAfter) {
      query.starting_after = startingAfter;
   }

   // Handle sorting if provided
   if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0];
      switch (sort.field) {
         case AssignmentNestedColumns.PROJECT_NAME:
            query.sort_by = FindAssignmentsSortBy.PROJECT_NAME;
            break;
         case AssignmentNestedColumns.START_DATE:
            query.sort_by = FindAssignmentsSortBy.ASSIGNMENT_START_DATE;
            break;
         case AssignmentNestedColumns.END_DATE:
            query.sort_by = FindAssignmentsSortBy.ASSIGNMENT_END_DATE;
            break;
      }
      query.sort_direction = sort.sort === "asc" ? Order.ASCENDING : Order.DESCENDING;
   }

   const data = await Assignment2Store.getAssignments(query).payload;
   return data;
}

export function useGetPeopleAlertConfig(peopleIds: string[]) {
   return useQuery({
      queryKey: ["people-alert-config", peopleIds],
      queryFn: () => PersonStore.getPeopleAlertConfig(peopleIds).payload,
      enabled: peopleIds.length > 0,
   });
}

export function useGetAssignmentDetails(assignmentId: string | null) {
   return useQuery({
      queryKey: ["assignment-details", assignmentId],
      queryFn: () => Assignment2Store.getAssignmentDetails(assignmentId!).payload,
      enabled: !!assignmentId,
   });
}

export function useTieredCategoryOptions(projectId: string | null) {
   return useQuery({
      queryKey: ["tieredCategories", projectId],
      queryFn: async () => {
         const response = await CategoriesStore.getProjectCategoriesForTieredSelect(projectId!)
            .payload;
         return response.data;
      },
      enabled: !!projectId,
   });
}
